import { __extends } from "tslib";
import { EventDispatcher } from '../events/EventDispatcher';
import { TransformEvent } from '../events/TransformEvent';
import { Matrix } from '../geom/Matrix';
import { Matrix3D } from '../geom/Matrix3D';
import { Point } from '../geom/Point';
import { Vector3D } from '../geom/Vector3D';
import { ColorTransform } from './ColorTransform';
/**
 * The Transform class provides access to color adjustment properties and two-
 * or three-dimensional transformation objects that can be applied to a display
 * object. During the transformation, the color or the orientation and position
 * of a display object is adjusted(offset) from the current values or
 * coordinates to new values or coordinates. The Transform class also collects
 * data about color and two-dimensional matrix transformations that are applied
 * to a display object and all of its parent objects. You can access these
 * combined transformations through the `concatenatedColorTransform` and
 * `concatenatedMatrix` properties.
 *
 * To apply color transformations: create a ColorTransform object, set the color
 * adjustments using the object's methods and properties, and then assign the
 * `colorTransformation` property of the `transform` property of the display
 * object to the new ColorTransformation object.
 *
 * To apply two-dimensional transformations: create a Matrix object, set the
 * matrix's two-dimensional transformation, and then assign the
 * `transform.matrix` property of the display object to the new Matrix object.
 *
 * To apply three-dimensional transformations: start with a three-dimensional
 * display object. A three-dimensional display object has a `z` property value
 * other than zero. You do not need to create the Matrix3D object. For all
 * three-dimensional objects, a Matrix3D object is created automatically when
 * you assign a `z` value to a display object. You can access the display
 * object's Matrix3D object through the display object's `transform` property.
 * Using the methods of the Matrix3D class, you can add to or modify the
 * existing transformation settings. Also, you can create a custom Matrix3D
 * object, set the custom Matrix3D object's transformation elements, and then
 * assign the new Matrix3D object to the display object using the
 * `transform.matrix` property.
 *
 * To modify a perspective projection of the stage or root object: use the
 * `transform.matrix` property of the root display object to gain access to the
 * PerspectiveProjection object. Or, apply different perspective projection
 * properties to a display object by setting the perspective projection
 * properties of the display object's parent. The child display object inherits
 * the new properties. Specifically, create a PerspectiveProjection object and
 * set its properties, then assign the PerspectiveProjection object to the
 * `perspectiveProjection` property of the parent display object's `transform`
 * property. The specified projection transformation then applies to all the
 * display object's three-dimensional children.
 *
 * Since both PerspectiveProjection and Matrix3D objects perform perspective
 * transformations, do not assign both to a display object at the same time. Use
 * the PerspectiveProjection object for focal length and projection center
 * changes. For more control over the perspective transformation, create a
 * perspective projection Matrix3D object.
 */
var Transform = /** @class */ (function (_super) {
    __extends(Transform, _super);
    function Transform(rawData, concatenatedMatrix3D) {
        if (rawData === void 0) { rawData = null; }
        if (concatenatedMatrix3D === void 0) { concatenatedMatrix3D = null; }
        var _this = _super.call(this) || this;
        _this._inverseConcatenatedMatrix3D = new Matrix3D();
        _this._matrix = null;
        _this._rotation = new Vector3D();
        _this._scale = new Vector3D(1, 1, 1);
        _this._skew = new Vector3D();
        //temp vector used in global to local
        _this._tempVector3D = new Vector3D();
        _this._rawData = rawData || new Float32Array(24);
        //create the view for matrix3D
        _this._matrix3D = new Matrix3D(new Float32Array(_this._rawData.buffer, 0, 16));
        //create the view for colorTransform
        _this._colorTransform = new ColorTransform(new Float32Array(_this._rawData.buffer, 64, 8));
        //create the concatenatedMatrix3D if required
        _this._concatenatedMatrix3D = concatenatedMatrix3D || _this._matrix3D;
        if (rawData == null) {
            _this._matrix3D.identity();
            _this._colorTransform.clear();
        }
        // Cached vector of transformation components used when
        // recomposing the transform matrix in updateTransform()
        _this._components = new Array(4);
        _this._components[1] = _this._rotation;
        _this._components[2] = _this._skew;
        _this._components[3] = _this._scale;
        _this.invalidateComponents();
        return _this;
    }
    Object.defineProperty(Transform.prototype, "backVector", {
        /**
         *
         */
        get: function () {
            if (!this._backVector)
                this._backVector = new Vector3D();
            this._matrix3D.copyColumnTo(2, this._backVector, true);
            return this._backVector;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "colorTransform", {
        /**
         * A ColorTransform object containing values that universally adjust the
         * colors in the display object.
         *
         * @throws TypeError The colorTransform is null when being set
         */
        get: function () {
            return this._colorTransform;
        },
        set: function (val) {
            if (val) {
                var sourceData = val._rawData, targetData = this._colorTransform._rawData;
                targetData[0] = sourceData[0];
                targetData[1] = sourceData[1];
                targetData[2] = sourceData[2];
                targetData[3] = sourceData[3];
                targetData[4] = sourceData[4];
                targetData[5] = sourceData[5];
                targetData[6] = sourceData[6];
                targetData[7] = sourceData[7];
            }
            else {
                this._colorTransform.clear();
            }
            this.invalidateColorTransform();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "concatenatedMatrix", {
        /**
         * A Matrix object representing the combined transformation matrixes of the
         * display object and all of its parent objects, back to the root level. If
         * different transformation matrixes have been applied at different levels,
         * all of those matrixes are concatenated into one matrix for this property.
         * Also, for resizeable SWF content running in the browser, this property
         * factors in the difference between stage coordinates and window
         * coordinates due to window resizing. Thus, the property converts local
         * coordinates to window coordinates, which may not be the same coordinate
         * space as that of the Stage.
         */
        get: function () {
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "concatenatedMatrix3D", {
        /**
         * A Matrix object representing the combined transformation matrixes of the
         * display object and all of its parent objects, back to the root level. If
         * different transformation matrixes have been applied at different levels,
         * all of those matrixes are concatenated into one matrix for this property.
         * Also, for resizeable SWF content running in the browser, this property
         * factors in the difference between stage coordinates and window
         * coordinates due to window resizing. Thus, the property converts local
         * coordinates to window coordinates, which may not be the same coordinate
         * space as that of the Scene.
         */
        get: function () {
            if (this._concatenatedMatrix3DDirty) {
                this._concatenatedMatrix3DDirty = false;
                //in cases where concatenated matrix has not been supplied
                if (this._concatenatedMatrix3D == this._matrix3D && this._matrix3DDirty)
                    this.updateMatrix3D();
                if (!this._updateConcatenatedMatrix3D) {
                    this._updateConcatenatedMatrix3D = new TransformEvent(TransformEvent.UPDATE_CONCATENATED_MATRIX3D, this);
                }
                this.dispatchEvent(this._updateConcatenatedMatrix3D);
            }
            return this._concatenatedMatrix3D;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "inverseConcatenatedMatrix3D", {
        /**
         *
         */
        get: function () {
            if (this._inverseConcatenatedMatrix3DDirty) {
                this._inverseConcatenatedMatrix3DDirty = false;
                this._inverseConcatenatedMatrix3D.copyFrom(this.concatenatedMatrix3D);
                this._inverseConcatenatedMatrix3D.invert();
            }
            return this._inverseConcatenatedMatrix3D;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "downVector", {
        /**
         *
         */
        get: function () {
            if (!this._downVector)
                this._downVector = new Vector3D();
            this._matrix3D.copyColumnTo(1, this._downVector, true);
            return this._downVector;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "forwardVector", {
        /**
         *
         */
        get: function () {
            if (!this._forwardVector)
                this._forwardVector = new Vector3D();
            this._matrix3D.copyColumnTo(2, this._forwardVector);
            return this._forwardVector;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "leftVector", {
        /**
         *
         */
        get: function () {
            if (!this._leftVector)
                this._leftVector = new Vector3D();
            this._matrix3D.copyColumnTo(0, this._backVector, true);
            return this._leftVector;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "matrix", {
        /**
         * A Matrix object containing values that alter the scaling, rotation, and
         * translation of the display object. If the `matrix` property is set to a
         * value (not `null`), the `matrix3D` property is `null`. And if the
         * `matrix3D` property is set to a value(not `null`), the `matrix` property
         * is `null`.
         *
         * @throws TypeError The matrix is null when being set
         */
        get: function () {
            // console.warn("deprecated! matrix3D cannot  be converted to 2D matrix, get matrix3D instead");
            // return null;
            if (!this._matrix) {
                this._matrix = new Matrix();
            }
            if (this._matrix3DDirty)
                this.updateMatrix3D();
            this._matrix.a = this._matrix3D._rawData[0];
            this._matrix.b = this._matrix3D._rawData[1];
            this._matrix.c = this._matrix3D._rawData[4];
            this._matrix.d = this._matrix3D._rawData[5];
            this._matrix.tx = this._matrix3D._rawData[12];
            this._matrix.ty = this._matrix3D._rawData[13];
            return this._matrix;
        },
        set: function (value) {
            if (!value) {
                return;
            }
            // console.warn("deprecated. 2d matrix can`n convert to 3D matrix, set matrix3D instead");
            // this._matrix = value;
            this._matrix3D.identity();
            this._matrix3D._rawData[0] = value.a;
            this._matrix3D._rawData[1] = value.b;
            this._matrix3D._rawData[4] = value.c;
            this._matrix3D._rawData[5] = value.d;
            this._matrix3D._rawData[12] = value.tx;
            this._matrix3D._rawData[13] = value.ty;
            this.invalidateComponents();
            this.invalidateConcatenatedMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "matrix3D", {
        /**
         * Provides access to the Matrix3D object of a three-dimensional display
         * object. The Matrix3D object represents a transformation matrix that
         * determines the display object's position and orientation. A Matrix3D
         * object can also perform perspective projection.
         *
         * If the `matrix` property is set to a value(not `null`), the `matrix3D`
         * property is `null`. And if the `matrix3D` property is set to a value(not
         * `null`), the `matrix` property is `null`.
         */
        get: function () {
            if (this._matrix3DDirty)
                this.updateMatrix3D();
            return this._matrix3D;
        },
        set: function (val) {
            if (!val) {
                this._matrix3D.identity();
            }
            else {
                var sourceData = val._rawData;
                var targetData = this._matrix3D._rawData;
                targetData[0] = sourceData[0];
                targetData[1] = sourceData[1];
                targetData[2] = sourceData[2];
                targetData[3] = sourceData[3];
                targetData[4] = sourceData[4];
                targetData[5] = sourceData[5];
                targetData[6] = sourceData[6];
                targetData[7] = sourceData[7];
                targetData[8] = sourceData[8];
                targetData[9] = sourceData[9];
                targetData[10] = sourceData[10];
                targetData[11] = sourceData[11];
                targetData[12] = sourceData[12];
                targetData[13] = sourceData[13];
                targetData[14] = sourceData[14];
                targetData[15] = sourceData[15];
            }
            this.invalidateComponents();
            this.invalidateConcatenatedMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "pixelBounds", {
        /**
         * A Rectangle object that defines the bounding rectangle of the display
         * object on the stage.
         */
        get: function () {
            return this._pixelBounds;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "position", {
        /**
         * Defines the position of the 3d object, relative to the local coordinates
         * of the parent `ObjectContainer3D`.
         */
        get: function () {
            return this._matrix3D.position;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "rightVector", {
        /**
         *
         */
        get: function () {
            if (!this._rightVector)
                this._rightVector = new Vector3D();
            this._matrix3D.copyColumnTo(0, this._rightVector);
            return this._rightVector;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "rotation", {
        /**
         * Defines the rotation of the 3d object, relative to the local coordinates
         * of the parent `ObjectContainer3D` .
         */
        get: function () {
            if (this._componentsDirty)
                this._updateComponents();
            return this._rotation;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "scale", {
        /**
         * Defines the scale of the 3d object, relative to the local coordinates of
         * the parent `ObjectContainer3D` .
         */
        get: function () {
            if (this._componentsDirty)
                this._updateComponents();
            return this._scale;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "skew", {
        /**
         * Defines the scale of the 3d object, relative to the local coordinates of
         * the parent `ObjectContainer3D` .
         */
        get: function () {
            if (this._componentsDirty)
                this._updateComponents();
            return this._skew;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Transform.prototype, "upVector", {
        /**
         *
         */
        get: function () {
            if (!this._upVector)
                this._upVector = new Vector3D();
            this._matrix3D.copyColumnTo(1, this._upVector);
            return this._upVector;
        },
        enumerable: true,
        configurable: true
    });
    /**
     *
     * @param value
     */
    Transform.prototype.append = function (value) {
        console.log("append(" + value + ") is not implemented yet in core/Transform");
    };
    /**
     *
     */
    Transform.prototype.clearColorTransform = function () {
        if (!this._colorTransform)
            return;
        this._colorTransform.clear();
        this.invalidateColorTransform();
    };
    /**
     *
     */
    Transform.prototype.clearMatrix3D = function () {
        this._matrix3D.identity();
        this.invalidateComponents();
    };
    /**
     *
     */
    Transform.prototype.clone = function () {
        var transform = new Transform();
        this.copyRawDataTo(transform);
        return transform;
    };
    /**
     *
     * @param transform
     */
    Transform.prototype.copyRawDataTo = function (transform) {
        if (this._matrix3DDirty)
            this.updateMatrix3D();
        var targetData = transform._rawData;
        var sourceData = this._rawData;
        //Matrix3D data
        targetData[0] = sourceData[0];
        targetData[1] = sourceData[1];
        targetData[2] = sourceData[2];
        targetData[3] = sourceData[3];
        targetData[4] = sourceData[4];
        targetData[5] = sourceData[5];
        targetData[6] = sourceData[6];
        targetData[7] = sourceData[7];
        targetData[8] = sourceData[8];
        targetData[9] = sourceData[9];
        targetData[10] = sourceData[10];
        targetData[11] = sourceData[11];
        targetData[12] = sourceData[12];
        targetData[13] = sourceData[13];
        targetData[14] = sourceData[14];
        targetData[15] = sourceData[15];
        //ColorTransform data
        targetData[16] = sourceData[16];
        targetData[17] = sourceData[17];
        targetData[18] = sourceData[18];
        targetData[19] = sourceData[19];
        targetData[20] = sourceData[20];
        targetData[21] = sourceData[21];
        targetData[22] = sourceData[22];
        targetData[23] = sourceData[23];
        this.invalidateComponents();
        this.invalidateColorTransform();
    };
    /**
     *
     */
    Transform.prototype.dispose = function () {
    };
    /**
     * Converts the `point` object from the Stage(global) coordinates to the
     * display object's(local) coordinates.
     *
     * To use this method, first create an instance of the Point class. The _x_
     * and _y_ values that you assign represent global coordinates because they
     * relate to the origin(0,0) of the main display area. Then pass the Point
     * instance as the parameter to the `globalToLocal()` method. The method
     * returns a new Point object with _x_ and _y_ values that relate to the
     * origin of the display object instead of the origin of the Stage.
     *
     * @param point An object created with the Point class. The Point object
     *              specifies the _x_ and _y_ coordinates as properties.
     * @return A Point object with coordinates relative to the display object.
     */
    Transform.prototype.globalToLocal = function (point, target) {
        if (target === void 0) { target = null; }
        this._tempVector3D.setTo(point.x, point.y, 0);
        //console.log("this._tempVector3D", this._tempVector3D);
        //console.log("this._transform.inverseConcatenatedMatrix3D", this._transform.inverseConcatenatedMatrix3D);
        var pos = this.inverseConcatenatedMatrix3D.transformVector(this._tempVector3D, this._tempVector3D);
        //console.log("pos", pos);
        if (!target)
            target = new Point();
        target.x = pos.x;
        target.y = pos.y;
        return target;
    };
    /**
     * Converts a two-dimensional point from the Scene(global) coordinates to a
     * three-dimensional display object's(local) coordinates.
     *
     * <p>To use this method, first create an instance of the Vector3D class. The x,
     * y and z values that you assign to the Vector3D object represent global
     * coordinates because they are relative to the origin(0,0,0) of the scene. Then
     * pass the Vector3D object to the <code>globalToLocal3D()</code> method as the
     * <code>position</code> parameter.
     * The method returns three-dimensional coordinates as a Vector3D object
     * containing <code>x</code>, <code>y</code>, and <code>z</code> values that
     * are relative to the origin of the three-dimensional display object.</p>
     *
     * @param point A Vector3D object representing global x, y and z coordinates in
     *              the scene.
     * @return A Vector3D object with coordinates relative to the three-dimensional
     *         display object.
     */
    Transform.prototype.globalToLocal3D = function (position) {
        return this.inverseConcatenatedMatrix3D.transformVector(position);
    };
    Transform.prototype.invalidateColorTransform = function () {
        if (!this._invalidateColorTransform) {
            this._invalidateColorTransform = new TransformEvent(TransformEvent.INVALIDATE_COLOR_TRANSFORM, this);
        }
        this.dispatchEvent(this._invalidateColorTransform);
    };
    Transform.prototype.invalidateComponents = function () {
        this.invalidatePosition();
        this._componentsDirty = true;
    };
    Transform.prototype.invalidateConcatenatedMatrix3D = function () {
        if (this._concatenatedMatrix3DDirty)
            return;
        this._concatenatedMatrix3DDirty = true;
        this._inverseConcatenatedMatrix3DDirty = true;
        if (!this._invalidateConcatenatedMatrix3D) {
            this._invalidateConcatenatedMatrix3D =
                new TransformEvent(TransformEvent.INVALIDATE_CONCATENATED_MATRIX3D, this);
        }
        this.dispatchEvent(this._invalidateConcatenatedMatrix3D);
    };
    /**
     * Invalidates the 3D transformation matrix, causing it to be updated upon the next request
     *
     * @private
     */
    Transform.prototype.invalidateMatrix3D = function () {
        if (this._concatenatedMatrix3D == this._matrix3D)
            this.invalidateConcatenatedMatrix3D();
        if (this._matrix3DDirty)
            return;
        this._matrix3DDirty = true;
        if (!this._invalidateMatrix3D) {
            this._invalidateMatrix3D = new TransformEvent(TransformEvent.INVALIDATE_MATRIX3D, this);
        }
        this.dispatchEvent(this._invalidateMatrix3D);
    };
    /**
     *
     */
    Transform.prototype.invalidatePosition = function () {
        if (this._concatenatedMatrix3D == this._matrix3D)
            this.invalidateConcatenatedMatrix3D();
        this._matrix3D.invalidatePosition();
        if (!this._invalidateMatrix3D) {
            this._invalidateMatrix3D = new TransformEvent(TransformEvent.INVALIDATE_MATRIX3D, this);
        }
        this.dispatchEvent(this._invalidateMatrix3D); //stricty speaking, this should be UPDATE_MATRIX3D
    };
    /**
     * Converts the `point` object from the display object's(local) coordinates
     * to the Stage(global) coordinates.
     *
     * This method allows you to convert any given _x_ and _y_ coordinates from
     * values that are relative to the origin(0,0) of a specific display
     * object(local coordinates) to values that are relative to the origin of
     * the Stage(global coordinates).
     *
     * To use this method, first create an instance of the Point class. The _x_
     * and _y_ values that you assign represent local coordinates because they
     * relate to the origin of the display object.
     *
     * You then pass the Point instance that you created as the parameter to the
     * `localToGlobal()` method. The method returns a new Point object with _x_
     * and _y_ values that relate to the origin of the Stage instead of the
     * origin of the display object.
     *
     * @param point The name or identifier of a point created with the Point
     *              class, specifying the _x_ and _y_ coordinates as properties.
     * @return A Point object with coordinates relative to the Stage.
     */
    Transform.prototype.localToGlobal = function (point, target) {
        if (target === void 0) { target = null; }
        this._tempVector3D.setTo(point.x, point.y, 0);
        var pos = this.concatenatedMatrix3D.transformVector(this._tempVector3D, this._tempVector3D);
        if (!target)
            target = new Point();
        target.x = pos.x;
        target.y = pos.y;
        return target;
    };
    /**
     * Rotates the 3d object around to face a point defined relative to the
     * local coordinates of the parent `ObjectContainer3D` .
     *
     * @param target The vector defining the point to be looked at.
     * @param upAxis An optional vector used to define the desired up
     *               orientation of the 3d object after rotation has occurred.
     */
    Transform.prototype.lookAt = function (position, upAxis) {
        if (upAxis === void 0) { upAxis = null; }
        if (upAxis == null)
            upAxis = Vector3D.Y_AXIS;
        var vec = Matrix3D.getPointAtMatrix(new Vector3D(), position.subtract(this._matrix3D.position), upAxis, Matrix3D.CALCULATION_MATRIX)
            .decompose()[1];
        this.rotateTo(vec.x, vec.y, vec.z);
    };
    /**
     * Moves the 3d object backwards along it's local z axis
     *
     * @param    distance    The length of the movement
     */
    Transform.prototype.moveBackward = function (distance) {
        this.translateLocal(Vector3D.Z_AXIS, -distance);
    };
    /**
     * Moves the 3d object backwards along it's local y axis
     *
     * @param    distance    The length of the movement
     */
    Transform.prototype.moveDown = function (distance) {
        this.translateLocal(Vector3D.Y_AXIS, -distance);
    };
    /**
     * Moves the 3d object forwards along it's local z axis
     *
     * @param    distance    The length of the movement
     */
    Transform.prototype.moveForward = function (distance) {
        this.translateLocal(Vector3D.Z_AXIS, distance);
    };
    /**
     * Moves the 3d object backwards along it's local x axis
     *
     * @param    distance    The length of the movement
     */
    Transform.prototype.moveLeft = function (distance) {
        this.translateLocal(Vector3D.X_AXIS, -distance);
    };
    /**
     * Moves the 3d object forwards along it's local x axis
     *
     * @param    distance    The length of the movement
     */
    Transform.prototype.moveRight = function (distance) {
        this.translateLocal(Vector3D.X_AXIS, distance);
    };
    /**
     * Moves the 3d object directly to a point in space
     *
     * @param    dx        The amount of movement along the local x axis.
     * @param    dy        The amount of movement along the local y axis.
     * @param    dz        The amount of movement along the local z axis.
     */
    Transform.prototype.moveTo = function (dx, dy, dz) {
        this._matrix3D._rawData[12] = dx;
        this._matrix3D._rawData[13] = dy;
        this._matrix3D._rawData[14] = dz;
        this.invalidatePosition();
    };
    /**
     * Moves the 3d object forwards along it's local y axis
     *
     * @param    distance    The length of the movement
     */
    Transform.prototype.moveUp = function (distance) {
        this.translateLocal(Vector3D.Y_AXIS, distance);
    };
    /**
     * Rotates the 3d object around it's local x-axis
     *
     * @param    angle        The amount of rotation in degrees
     */
    Transform.prototype.pitch = function (angle) {
        this.rotate(Vector3D.X_AXIS, angle);
    };
    /**
     * Rotates the 3d object around it's local z-axis
     *
     * @param    angle        The amount of rotation in degrees
     */
    Transform.prototype.roll = function (angle) {
        this.rotate(Vector3D.Z_AXIS, angle);
    };
    /**
     * Rotates the 3d object around an axis by a defined angle
     *
     * @param    axis        The vector defining the axis of rotation
     * @param    angle        The amount of rotation in degrees
     */
    Transform.prototype.rotate = function (axis, angle) {
        this.matrix3D.prependRotation(angle, axis);
        this.invalidateComponents();
    };
    /**
     * Rotates the 3d object directly to a euler angle
     *
     * @param ax The angle in degrees of the rotation around the x axis.
     * @param ay The angle in degrees of the rotation around the y axis.
     * @param az The angle in degrees of the rotation around the z axis.
     */
    Transform.prototype.rotateTo = function (ax, ay, az) {
        if (this._componentsDirty)
            this._updateComponents();
        this._rotation.x = ax;
        this._rotation.y = ay;
        this._rotation.z = az;
        this.invalidateMatrix3D();
    };
    /**
     *
     * @param sx
     * @param sy
     * @param sz
     */
    Transform.prototype.scaleTo = function (sx, sy, sz) {
        if (this._componentsDirty)
            this._updateComponents();
        this._scale.x = sx;
        this._scale.y = sy;
        this._scale.z = sz;
        this.invalidateMatrix3D();
    };
    /**
     *
     * @param sx
     * @param sy
     * @param sz
     */
    Transform.prototype.skewTo = function (sx, sy, sz) {
        if (this._componentsDirty)
            this._updateComponents();
        this._skew.x = sx;
        this._skew.y = sy;
        this._skew.z = sz;
        this.invalidateMatrix3D();
    };
    /**
     * Moves the 3d object along a vector by a defined length.
     *
     * @param axis     The vector defining the axis of movement.
     * @param distance The length of the movement.
     */
    Transform.prototype.translate = function (axis, distance) {
        var x = axis.x, y = axis.y, z = axis.z;
        var len = distance / Math.sqrt(x * x + y * y + z * z);
        this.matrix3D.appendTranslation(x * len, y * len, z * len);
        this.invalidatePosition();
    };
    /**
     * Moves the 3d object along a vector by a defined length.
     *
     * @param axis     The vector defining the axis of movement.
     * @param distance The length of the movement.
     */
    Transform.prototype.translateLocal = function (axis, distance) {
        var x = axis.x, y = axis.y, z = axis.z;
        var len = distance / Math.sqrt(x * x + y * y + z * z);
        this.matrix3D.prependTranslation(x * len, y * len, z * len);
        this.invalidatePosition();
    };
    /**
     *
     */
    Transform.prototype.updateMatrix3D = function () {
        this._matrix3D.recompose(this._components);
        this._matrix3DDirty = false;
    };
    /**
     * Rotates the 3d object around it's local y-axis
     *
     * @param angle The amount of rotation in degrees
     */
    Transform.prototype.yaw = function (angle) {
        this.rotate(Vector3D.Y_AXIS, angle);
    };
    Transform.prototype._updateComponents = function () {
        var elements = this._matrix3D.decompose();
        var vec;
        vec = elements[1];
        this._rotation.x = vec.x;
        this._rotation.y = vec.y;
        this._rotation.z = vec.z;
        vec = elements[2];
        this._skew.x = vec.x;
        this._skew.y = vec.y;
        this._skew.z = vec.z;
        vec = elements[3];
        this._scale.x = vec.x;
        this._scale.y = vec.y;
        this._scale.z = vec.z;
        this._componentsDirty = false;
    };
    return Transform;
}(EventDispatcher));
export { Transform };
